"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect } from "react";
import { useInView } from "@/hooks/useInView";
import { useOptimizedVideo } from "@/hooks/useOptimizedVideo";
import { useHlsPlayer } from "@/hooks/useHlsPlayer";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { PlayIcon } from "@heroicons/react/24/solid";
import { InlineMediaActions } from "./MediaActionToolbar";
import { useMediaActions } from "./useMediaActions";
interface Props {
  readonly src: string;
  readonly mimeType?: string | undefined;
  readonly showControls?: boolean | undefined;
}
const MediaDisplayVideo: React.FC<Props> = t0 => {
  const $ = _c(52);
  const {
    src,
    mimeType,
    showControls: t1
  } = t0;
  const showControls = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      threshold: 0.1
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [wrapperRef, inView] = useInView(t2);
  const {
    isApp
  } = useDeviceInfo();
  let t3;
  if ($[1] !== mimeType || $[2] !== src) {
    t3 = {
      url: src,
      fallbackFileName: "video",
      dialogTitle: "Save video",
      mimeType
    };
    $[1] = mimeType;
    $[2] = src;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    downloadMedia,
    isDownloading,
    openLabel,
    openMedia
  } = useMediaActions(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      pollInterval: 15000,
      maxRetries: 8,
      preferHls: true,
      exponentialBackoff: false
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    playableUrl,
    isHls
  } = useOptimizedVideo(src, t4);
  const t5 = inView && !isApp;
  let t6;
  if ($[5] !== isHls || $[6] !== playableUrl || $[7] !== src || $[8] !== t5) {
    t6 = {
      src: playableUrl,
      isHls,
      fallbackSrc: src,
      autoPlay: t5
    };
    $[5] = isHls;
    $[6] = playableUrl;
    $[7] = src;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    videoRef,
    isLoading
  } = useHlsPlayer(t6);
  const showNativeControls = showControls && !isApp;
  let t7;
  let t8;
  if ($[10] !== videoRef) {
    t7 = () => {
      const vid = videoRef.current;
      if (!vid) {
        return;
      }
      vid.setAttribute("webkit-playsinline", "true");
      vid.setAttribute("x5-playsinline", "true");
    };
    t8 = [videoRef];
    $[10] = videoRef;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[13] !== inView || $[14] !== isApp || $[15] !== isLoading || $[16] !== videoRef) {
    t9 = () => {
      const vid_0 = videoRef.current;
      if (!vid_0 || isLoading) {
        return;
      }
      if (!inView || isApp) {
        vid_0.pause();
      } else {
        vid_0.play().catch(_temp);
      }
    };
    t10 = [inView, isApp, isLoading, videoRef];
    $[13] = inView;
    $[14] = isApp;
    $[15] = isLoading;
    $[16] = videoRef;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[19] !== isApp || $[20] !== videoRef) {
    t11 = () => {
      if (!isApp) {
        return;
      }
      const pauseWhenFullscreenCloses = () => {
        const vid_1 = videoRef.current;
        if (!vid_1 || document.fullscreenElement === vid_1) {
          return;
        }
        vid_1.pause();
      };
      document.addEventListener("fullscreenchange", pauseWhenFullscreenCloses);
      return () => {
        document.removeEventListener("fullscreenchange", pauseWhenFullscreenCloses);
      };
    };
    t12 = [isApp, videoRef];
    $[19] = isApp;
    $[20] = videoRef;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  let t13;
  if ($[23] !== showControls || $[24] !== videoRef) {
    t13 = () => {
      if (showControls) {
        return;
      }
      const vid_2 = videoRef.current;
      if (!vid_2) {
        return;
      }
      if (vid_2.paused) {
        vid_2.play().catch(_temp2);
        return;
      }
      vid_2.pause();
    };
    $[23] = showControls;
    $[24] = videoRef;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleVideoClick = t13;
  let t14;
  if ($[26] !== videoRef) {
    t14 = () => {
      const vid_3 = videoRef.current;
      if (!vid_3) {
        return;
      }
      vid_3.play().catch(_temp3);
      vid_3.requestFullscreen().catch(_temp4);
    };
    $[26] = videoRef;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const enterFullscreenAndPlay = t14;
  let t15;
  if ($[28] !== enterFullscreenAndPlay || $[29] !== handleVideoClick || $[30] !== isApp || $[31] !== showControls) {
    t15 = () => {
      if (showControls && isApp) {
        enterFullscreenAndPlay();
        return;
      }
      handleVideoClick();
    };
    $[28] = enterFullscreenAndPlay;
    $[29] = handleVideoClick;
    $[30] = isApp;
    $[31] = showControls;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== showNativeControls || $[34] !== t15 || $[35] !== videoRef) {
    t16 = <video ref={videoRef} onClick={t15} className="tw-h-auto tw-max-h-64 tw-w-full tw-rounded-xl tw-object-contain" muted={true} loop={true} controls={showNativeControls} controlsList="nodownload noplaybackrate" playsInline={true} preload="auto" />;
    $[33] = showNativeControls;
    $[34] = t15;
    $[35] = videoRef;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== enterFullscreenAndPlay || $[38] !== isApp || $[39] !== showControls) {
    t17 = showControls && isApp && <button type="button" aria-label="Play video" title="Play video" onClick={event => {
      event.stopPropagation();
      enterFullscreenAndPlay();
    }} className="tw-absolute tw-left-1/2 tw-top-1/2 tw-z-20 tw-flex tw-size-16 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-iron-700/75 tw-text-white tw-shadow-lg tw-shadow-black/30 tw-backdrop-blur-sm"><PlayIcon className="tw-ml-1 tw-size-8" aria-hidden="true" /></button>;
    $[37] = enterFullscreenAndPlay;
    $[38] = isApp;
    $[39] = showControls;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== downloadMedia || $[42] !== isDownloading || $[43] !== openLabel || $[44] !== openMedia || $[45] !== showControls) {
    t18 = showControls && <InlineMediaActions variant="video" onDownload={downloadMedia} onOpen={openMedia} openLabel={openLabel} isDownloading={isDownloading} />;
    $[41] = downloadMedia;
    $[42] = isDownloading;
    $[43] = openLabel;
    $[44] = openMedia;
    $[45] = showControls;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== wrapperRef) {
    t19 = <div ref={wrapperRef} className="tw-relative tw-max-h-64 tw-min-h-[200px] tw-w-full tw-overflow-hidden tw-rounded-xl tw-bg-black">{t16}{t17}{t18}</div>;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = wrapperRef;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
};
export default React.memo(MediaDisplayVideo);
function _temp() {}
function _temp2() {}
function _temp3() {}
function _temp4() {}